import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Notification, NotificationFilled } from '@carbon/react/icons';
import { usePolling } from 'lib/hooks/usePolling';
import { subscribe } from 'lib/pusher';
import {
  isNotificationPanelOpenAtom,
  unreadCountAtom,
  unseenCountAtom,
} from './atoms';
import { useDoNotDisturb } from './use-do-not-disturb';
import { NotificationPanel } from './notification-panel';
import { useNotifications } from './use-notifications';
import styles from './notification-feed.module.scss';

/** Slow enough to be invisible in the query log, fast enough that the badge is not stale. */
const POLL_INTERVAL_MS = 60_000;

/** Past this the badge would break the header layout, and the exact number stops mattering. */
const BADGE_CAP = 99;

/**
 * The bell and its unread badge, rendered in the app header.
 *
 * Polls rather than relying on the socket alone. A dropped Pusher connection is silent —
 * the badge would simply stop updating and nobody would know — so the poll is the floor on
 * how wrong it can get, and the socket is what makes it feel immediate.
 *
 * @return {JSX.Element} The bell button, its badge, and the panel when open.
 */
export const NotificationBell: FC = () => {
  const unreadCount = useAtomValue(unreadCountAtom);
  const unseenCount = useAtomValue(unseenCountAtom);
  const [isOpen, setIsOpen] = useAtom(isNotificationPanelOpenAtom);
  const {
    rows,
    totalCount,
    isLoading,
    channel,
    fetchPage,
    markAllSeen,
    markRead,
    markAllRead,
  } = useNotifications();
  const { isEnabled: doNotDisturb, toggle: setDoNotDisturb } =
    useDoNotDisturb();

  // Panel-only, and deliberately not persisted: it is a way to look through a busy list
  // right now, not a standing preference. Reopening the bell starts from everything again.
  const [unreadOnly, setUnreadOnly] = useState(false);

  // Two things decide the badge:
  //
  //   unseen, not unread — opening the panel silences it, while rows you have not dealt
  //     with stay tinted until you do. Matches how Slack and Jira behave.
  //   Do Not Disturb hides it without muting the feed — rows keep arriving and the panel
  //     keeps showing them, because someone returning from DND still needs to know what
  //     they missed.
  const badgeCount = doNotDisturb ? 0 : unseenCount;

  const containerRef = useRef<HTMLDivElement>(null);

  usePolling({
    // Result discarded: the poll only needs the state fetchPage sets. The returned rows
    // matter to the open handler below, which marks exactly what it was handed.
    fn: async () => {
      await fetchPage({ unreadOnly });
    },
    intervalMs: POLL_INTERVAL_MS,
    // pauseOnHidden is the hook's default: no reason to poll a tab nobody is looking at.
  });

  // Live updates. The event carries no payload by design — the channel is not private, so
  // it says only "something changed, refetch" and the content comes back over the
  // authenticated API. The poll above is what makes a dropped socket a delay rather than
  // silence.
  useEffect(() => {
    if (!channel) return undefined;

    const subscription = subscribe(channel);
    subscription.bind('notification', () => fetchPage({ unreadOnly }));

    return () => {
      subscription.unbind('notification');
      subscription.unsubscribe();
    };
  }, [channel, fetchPage, unreadOnly]);

  // Clicking anywhere else closes the panel. Registered only while it is open so the app
  // is not carrying a document-level listener for a dropdown that is shut.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node))
        setIsOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onDocumentClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocumentClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen, setIsOpen]);

  // Open state only. The fetch that follows an open lives in the effect below — doing it
  // here as well raced it: fetchPage aborts whatever is in flight and resolves the loser
  // with an empty array, so whichever fetch lost handed markAllSeen no ids and the badge
  // stayed lit after the user had plainly seen the rows.
  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // The one fetch behind an open panel, and the one place the badge is silenced.
  //
  // Refetched rather than trusting the last poll: the panel is the moment the user is
  // actually looking, and up to 60 seconds stale is visible there. Toggling the filter
  // refetches too rather than filtering in place, because the server owns paging and a
  // client-side filter would leave the panel showing a fraction of its 20 rows.
  //
  // markAllSeen is chained onto this fetch so it marks exactly the rows that were
  // delivered — not whatever the last poll happened to be holding — which is what keeps
  // the badge alive for older rows this panel never showed.
  //
  // The fallback matters: fetchPage shares one abort controller with the 60-second poll and
  // the Pusher subscription, so either firing while the panel is opening aborts this read,
  // which resolves empty. markAllSeen([]) then returns without doing anything and the badge
  // stays lit over a panel the user is looking straight at. The rows already on screen are
  // what they saw, so those are what gets silenced.
  useEffect(() => {
    if (!isOpen) return;

    fetchPage({ unreadOnly }).then((delivered) => {
      const seen = delivered.length > 0 ? delivered : rows;

      markAllSeen(seen.map((row) => row.id));
    });
    // `rows` is deliberately not a dependency: it changes on every poll, and re-running this
    // on each one would re-mark rows already marked. It is read only as a fallback for the
    // open that is happening now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unreadOnly, fetchPage, markAllSeen]);

  const badge = badgeCount > BADGE_CAP ? `${BADGE_CAP}+` : String(badgeCount);

  return (
    <div className={styles.bellContainer} ref={containerRef}>
      <button
        type="button"
        className={styles.bellButton}
        onClick={toggle}
        aria-label={
          badgeCount > 0 ? `Notifications, ${badgeCount} new` : 'Notifications'
        }
        aria-expanded={isOpen}
        title="Notifications"
      >
        {badgeCount > 0 ? (
          <NotificationFilled size={16} />
        ) : (
          <Notification size={16} />
        )}
        {badgeCount > 0 ? <span className={styles.badge}>{badge}</span> : null}
      </button>

      {isOpen ? (
        <NotificationPanel
          rows={rows}
          isLoading={isLoading}
          unreadCount={unreadCount}
          totalCount={totalCount}
          doNotDisturb={doNotDisturb}
          onDoNotDisturbChange={setDoNotDisturb}
          unreadOnly={unreadOnly}
          onUnreadOnlyChange={setUnreadOnly}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onClose={() => setIsOpen(false)}
        />
      ) : null}
    </div>
  );
};
